/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Universal Shared Types & Schemas for Admin KeyPool Monitor (Task 11.2.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Zod schema for a single KeyPool key diagnostic status.
 */
export const AdminKeyPoolItemSchema = z
  .object({
    id: z.string().openapi({ description: 'Key unique identifier', example: 'key-1-a8b2c4' }),
    provider: z.string().openapi({ description: 'Provider identifier', example: 'openai' }),
    providerName: z.string().optional().openapi({ description: 'Provider display name', example: 'OpenAI' }),
    maskedKey: z.string().openapi({ description: 'Masked API key for secure inspection', example: 'sk-proj...cdef' }),
    weight: z.number().default(1).openapi({ description: 'Load-balancing traffic weight', example: 1 }),
    status: z.enum(['active', 'quarantined', 'cooling']).openapi({ description: 'Operational health state', example: 'active' }),
    quarantinedUntil: z.number().nullable().optional().openapi({ description: 'Timestamp when quarantine expires' }),
    failCount: z.number().default(0).openapi({ description: 'Cumulative error count', example: 0 }),
    successCount: z.number().default(0).openapi({ description: 'Successful inference calls in window', example: 1240 }),
    rateLimit429Count1h: z.number().default(0).openapi({ description: 'HTTP 429 incidents in the past 1 hour', example: 0 }),
    successRate: z.number().default(100).openapi({ description: 'Success rate percentage (0-100)', example: 99.8 }),
    lastUsedAt: z.number().nullable().optional().openapi({ description: 'Timestamp of last dispatch' }),
  })
  .openapi('AdminKeyPoolItem');

export type AdminKeyPoolItem = z.infer<typeof AdminKeyPoolItemSchema>;

/**
 * Provider-level aggregate metrics in the pool.
 */
export const KeyPoolProviderSummarySchema = z
  .object({
    provider: z.string().openapi({ example: 'openai' }),
    providerName: z.string().openapi({ example: 'OpenAI' }),
    totalKeys: z.number().openapi({ example: 4 }),
    activeKeys: z.number().openapi({ example: 3 }),
    quarantinedKeys: z.number().openapi({ example: 1 }),
    avgSuccessRate: z.number().openapi({ example: 99.2 }),
    total429s1h: z.number().openapi({ example: 2 }),
  })
  .openapi('KeyPoolProviderSummary');

export type KeyPoolProviderSummary = z.infer<typeof KeyPoolProviderSummarySchema>;

/**
 * Complete KeyPool overview response schema.
 */
export const AdminKeyPoolOverviewSchema = z
  .object({
    totalKeys: z.number().openapi({ example: 18 }),
    activeKeys: z.number().openapi({ example: 16 }),
    quarantinedKeys: z.number().openapi({ example: 2 }),
    total429s1h: z.number().openapi({ example: 3 }),
    providers: z.array(KeyPoolProviderSummarySchema),
    keys: z.array(AdminKeyPoolItemSchema),
  })
  .openapi('AdminKeyPoolOverview');

export type AdminKeyPoolOverview = z.infer<typeof AdminKeyPoolOverviewSchema>;

/**
 * Request payload for adding a new API key into the system KeyPool.
 */
export const AddKeyPoolKeySchema = z
  .object({
    provider: z.string().min(1, 'Provider is required').openapi({ description: 'AI provider code (e.g. openai, anthropic)', example: 'openai' }),
    key: z.string().min(8, 'API Key must be at least 8 characters').openapi({ description: 'Plaintext upstream API Key', example: 'sk-proj-abc123xyz...' }),
    weight: z.number().min(1).max(10).default(1).openapi({ description: 'Traffic weight for round-robin dispatch', example: 1 }),
  })
  .openapi('AddKeyPoolKeyRequest');

export type AddKeyPoolKeyInput = z.infer<typeof AddKeyPoolKeySchema>;

/**
 * Request payload for manual quarantine.
 */
export const QuarantineKeySchema = z
  .object({
    durationMinutes: z.number().min(1).max(1440).default(10).openapi({ description: 'Quarantine duration in minutes', example: 15 }),
    reason: z.string().optional().openapi({ description: 'Diagnostic reason for quarantine', example: 'Rate limit 429 spike observed' }),
  })
  .openapi('QuarantineKeyRequest');

export type QuarantineKeyInput = z.infer<typeof QuarantineKeySchema>;

/**
 * Request payload for resetting circuit breaker.
 */
export const ResetCircuitSchema = z
  .object({
    provider: z.string().optional().openapi({ description: 'Target provider code, or omit to reset all pools', example: 'openai' }),
  })
  .openapi('ResetCircuitRequest');

export type ResetCircuitInput = z.infer<typeof ResetCircuitSchema>;
